// This side-effect script supports both classic `src` loading and
// `await import(url)` from an inline module script. In either case the API is
// published as `globalThis.DOMReconciler`.
(function (global) {
  "use strict";

  var ACTION_TYPES = {
    "style": true,
    "class:add": true,
    "remove": true,
    "rewrite": true,
    "text": true,
    "move": true
  };
  var MOVE_POSITIONS = { before: true, after: true, prepend: true, append: true };
  var REWRITE_MODES = { inner: true, outer: true };

  function assert(condition, message, ErrorType) {
    if (!condition) throw new (ErrorType || TypeError)(message);
  }

  function isQueryRoot(value) {
    return value && typeof value.querySelectorAll === "function" &&
      typeof value.addEventListener === "function";
  }

  function ownerDocument(root) {
    return root.nodeType === 9 ? root : root.ownerDocument;
  }

  function query(root, selector, includeRoot) {
    var results = Array.prototype.slice.call(root.querySelectorAll(selector));
    if (includeRoot && root.nodeType === 1 && root.matches(selector)) results.unshift(root);
    return results;
  }

  function normalizeClasses(value, ruleId) {
    var values;
    if (typeof value === "string") {
      values = value.trim() ? value.trim().split(/\s+/) : [];
    } else {
      assert(Array.isArray(value), "Rule \"" + ruleId + "\": classes must be a string or string array");
      values = value.slice();
    }
    var seen = Object.create(null);
    return values.filter(function (name) {
      assert(typeof name === "string" && name.length > 0 && !/\s/.test(name),
        "Rule \"" + ruleId + "\": invalid class token");
      if (seen[name]) return false;
      seen[name] = true;
      return true;
    });
  }

  function cssName(name) {
    if (name.indexOf("--") === 0) return name;
    return name.replace(/[A-Z]/g, function (letter) { return "-" + letter.toLowerCase(); });
  }

  function normalizeStyleValue(doc, property, value, ruleId) {
    if (value == null) return null;
    var style = doc.createElement("span").style;
    style.setProperty(property, String(value));
    var normalized = style.getPropertyValue(property);
    assert(String(value) === "" || normalized !== "",
      "Rule \"" + ruleId + "\": invalid value for style property \"" + property + "\"");
    return normalized;
  }

  function parseOuter(doc, html, ruleId) {
    var template = doc.createElement("template");
    template.innerHTML = html.trim();
    assert(template.content.childNodes.length === 1 && template.content.firstChild.nodeType === 1,
      "Rule \"" + ruleId + "\": outer rewrite must produce exactly one root element");
    return {
      html: template.content.firstElementChild.outerHTML,
      element: template.content.firstElementChild
    };
  }

  function normalizeInner(doc, html) {
    var template = doc.createElement("template");
    template.innerHTML = html;
    return template.innerHTML;
  }

  function validateRule(raw, root, existingIds, order) {
    assert(raw && typeof raw === "object" && !Array.isArray(raw), "Rule must be an object");
    assert(typeof raw.id === "string" && raw.id.trim() !== "", "Rule id must be a non-empty string");
    assert(!existingIds.has(raw.id), "Duplicate rule id: \"" + raw.id + "\"", Error);
    assert(typeof raw.selector === "string" && raw.selector.trim() !== "",
      "Rule \"" + raw.id + "\": selector must be a non-empty string");
    try { root.querySelector(raw.selector); } catch (error) {
      throw new SyntaxError("Rule \"" + raw.id + "\": invalid selector: " + error.message);
    }

    var match = raw.match == null ? "all" : raw.match;
    assert(match === "all" || match === "first",
      "Rule \"" + raw.id + "\": match must be \"all\" or \"first\"");
    var priority = raw.priority == null ? 0 : raw.priority;
    assert(typeof priority === "number" && isFinite(priority),
      "Rule \"" + raw.id + "\": priority must be a finite number");
    var actions = raw.actions == null ? [] : raw.actions;
    assert(Array.isArray(actions), "Rule \"" + raw.id + "\": actions must be an array");
    var normalizedActions = [];
    var counts = Object.create(null);

    actions.forEach(function (action, actionIndex) {
      assert(action && typeof action === "object" && !Array.isArray(action),
        "Rule \"" + raw.id + "\": action " + actionIndex + " must be an object");
      assert(ACTION_TYPES[action.type],
        "Rule \"" + raw.id + "\": unsupported action type \"" + action.type + "\"");
      counts[action.type] = (counts[action.type] || 0) + 1;
      var normalized = { type: action.type };

      if (action.type === "style") {
        assert(action.properties && typeof action.properties === "object" && !Array.isArray(action.properties),
          "Rule \"" + raw.id + "\": style properties must be an object");
        normalized.properties = {};
        Object.keys(action.properties).forEach(function (property) {
          assert(property.length > 0, "Rule \"" + raw.id + "\": style property cannot be empty");
          var value = action.properties[property];
          assert(value == null || typeof value === "string" || typeof value === "number",
            "Rule \"" + raw.id + "\": style values must be strings, numbers, or null");
          var normalizedProperty = cssName(property);
          normalized.properties[normalizedProperty] = normalizeStyleValue(
            ownerDocument(root), normalizedProperty, value, raw.id
          );
        });
      } else if (action.type === "class:add") {
        normalized.classes = normalizeClasses(action.classes, raw.id);
      } else if (action.type === "rewrite") {
        assert(REWRITE_MODES[action.mode],
          "Rule \"" + raw.id + "\": rewrite mode must be \"inner\" or \"outer\"");
        assert(typeof action.content === "string" || typeof action.content === "function",
          "Rule \"" + raw.id + "\": rewrite content must be a string or function");
        normalized.mode = action.mode;
        normalized.content = action.content;
        if (action.mode === "outer" && typeof action.content === "string") {
          parseOuter(ownerDocument(root), action.content, raw.id);
        }
        if (typeof action.content === "function") {
          query(root, raw.selector, true).forEach(function (element) {
            var first = action.content(element);
            var second = action.content(element);
            assert(typeof first === "string" && first === second,
              "Rule \"" + raw.id + "\": rewrite content function must return the same string twice", Error);
            if (action.mode === "outer") parseOuter(ownerDocument(root), first, raw.id);
          });
        }
      } else if (action.type === "text") {
        assert(typeof action.value === "string" || typeof action.value === "number",
          "Rule \"" + raw.id + "\": text value must be a string or number");
        normalized.value = String(action.value);
      } else if (action.type === "move") {
        assert(typeof action.destination === "string" && action.destination.trim() !== "",
          "Rule \"" + raw.id + "\": move destination must be a CSS selector string");
        try { root.querySelector(action.destination); } catch (error) {
          throw new SyntaxError("Rule \"" + raw.id + "\": invalid move destination: " + error.message);
        }
        assert(MOVE_POSITIONS[action.position],
          "Rule \"" + raw.id + "\": move position must be before, after, prepend, or append");
        assert(query(root, action.destination, true).length <= 1,
          "Rule \"" + raw.id + "\": move destination is ambiguous", Error);
        normalized.destination = action.destination;
        normalized.position = action.position;
      }
      normalizedActions.push(normalized);
    });

    assert(!counts.remove || actions.length === 1,
      "Rule \"" + raw.id + "\": remove must be the only action");
    var outerCount = normalizedActions.filter(function (action) {
      return action.type === "rewrite" && action.mode === "outer";
    }).length;
    assert(!outerCount || actions.length === 1,
      "Rule \"" + raw.id + "\": outer rewrite must be the only action");
    var innerCount = normalizedActions.filter(function (action) {
      return action.type === "rewrite" && action.mode === "inner";
    }).length;
    assert((counts.rewrite || 0) <= 1, "Rule \"" + raw.id + "\": only one rewrite action is allowed");
    assert((counts.text || 0) <= 1, "Rule \"" + raw.id + "\": only one text action is allowed");
    assert((counts.move || 0) <= 1, "Rule \"" + raw.id + "\": only one move action is allowed");
    assert(!(innerCount && counts.text),
      "Rule \"" + raw.id + "\": inner rewrite and text actions are incompatible");

    var hooks = raw.hooks == null ? {} : raw.hooks;
    assert(hooks && typeof hooks === "object" && !Array.isArray(hooks),
      "Rule \"" + raw.id + "\": hooks must be an object");
    ["beforeApply", "afterApply"].forEach(function (name) {
      assert(hooks[name] == null || typeof hooks[name] === "function",
        "Rule \"" + raw.id + "\": " + name + " hook must be a function");
    });

    return {
      id: raw.id,
      selector: raw.selector,
      match: match,
      priority: priority,
      actions: normalizedActions,
      hooks: { beforeApply: hooks.beforeApply, afterApply: hooks.afterApply },
      order: order
    };
  }

  function outranks(left, right) {
    if (left.rule.priority !== right.rule.priority) return left.rule.priority > right.rule.priority;
    if (left.rule.order !== right.rule.order) return left.rule.order < right.rule.order;
    return left.actionIndex > right.actionIndex;
  }

  function candidateValue(candidate) {
    if (candidate.kind === "style") return candidate.value;
    if (candidate.kind === "text") return candidate.value;
    if (candidate.kind === "inner" || candidate.kind === "outer") return candidate.html;
    if (candidate.kind === "move") return candidate.action.destination + "|" + candidate.action.position;
    return candidate.kind;
  }

  function choose(candidates, conflict, report) {
    if (!candidates.length) return null;
    var winner = candidates[0];
    candidates.slice(1).forEach(function (candidate) {
      if (outranks(candidate, winner)) {
        var previous = winner;
        winner = candidate;
        candidate = previous;
      }
      if (conflict && candidateValue(candidate) !== candidateValue(winner)) report(winner, candidate);
    });
    return winner;
  }

  function create(options) {
    options = options == null ? {} : options;
    assert(options && typeof options === "object" && !Array.isArray(options), "Options must be an object");
    var root = options.root == null ? global.document : options.root;
    assert(isQueryRoot(root), "root must be a Document, Element, or DocumentFragment");
    assert(options.onError == null || typeof options.onError === "function", "onError must be a function");
    var onError = options.onError;
    var rules = [];
    var ids = new Set();
    var nextOrder = 0;
    var observer = null;
    var running = false;
    var destroyed = false;
    var reconciling = false;
    var queuedReasons = [];
    var frameId = null;
    var api;

    function ensureAlive() {
      if (destroyed) throw new Error("DOMReconciler instance has been destroyed");
    }

    function dispatch(name, detail, target) {
      var eventTarget = target && typeof target.dispatchEvent === "function" ? target : root;
      eventTarget.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail }));
    }

    function reportError(error, detail) {
      if (onError) onError(error, detail);
      dispatch("domreconciler:error", detail, detail && detail.newElement);
    }

    function reportConflict(winner, loser, element, reason) {
      var error = new Error("Rule \"" + loser.rule.id + "\" conflicts with higher-ranked rule \"" + winner.rule.id + "\"");
      error.name = "DOMReconcilerConflictError";
      reportError(error, {
        ruleId: loser.rule.id,
        reason: reason,
        changedActions: [loser.action],
        oldElement: element,
        newElement: element,
        conflictWith: winner.rule.id,
        error: error
      });
    }

    function observe() {
      if (!running || !observer) return;
      observer.observe(root, { subtree: true, childList: true, characterData: true, attributes: true });
    }

    function schedule(reason) {
      if (!running || destroyed) return;
      if (queuedReasons.indexOf(reason) === -1) queuedReasons.push(reason);
      if (frameId != null) return;
      frameId = global.requestAnimationFrame(function () {
        frameId = null;
        var reasonText = queuedReasons.join(",") || "mutation";
        queuedReasons = [];
        performReconcile(reasonText);
      });
    }

    function resolveContent(rule, action, element, reason) {
      var first;
      var second;
      try {
        first = typeof action.content === "function" ? action.content(element) : action.content;
        second = typeof action.content === "function" ? action.content(element) : action.content;
        assert(typeof first === "string" && first === second,
          "Rule \"" + rule.id + "\": rewrite content function must return the same string twice", Error);
        if (action.mode === "outer") return parseOuter(ownerDocument(root), first, rule.id);
        return { html: normalizeInner(ownerDocument(root), first) };
      } catch (error) {
        reportError(error, {
          ruleId: rule.id,
          reason: reason,
          changedActions: [action],
          oldElement: element,
          newElement: element,
          error: error
        });
        return null;
      }
    }

    function collect(reason) {
      var selectorCache = new Map();
      var elementCandidates = new Map();

      rules.forEach(function (rule) {
        var matches = selectorCache.get(rule.selector);
        if (!matches) {
          matches = query(root, rule.selector, true);
          selectorCache.set(rule.selector, matches);
        }
        if (rule.match === "first") matches = matches.slice(0, 1);
        matches.forEach(function (element) {
          var candidates = elementCandidates.get(element);
          if (!candidates) {
            candidates = [];
            elementCandidates.set(element, candidates);
          }
          rule.actions.forEach(function (action, actionIndex) {
            var base = { rule: rule, action: action, actionIndex: actionIndex, element: element };
            if (action.type === "style") {
              Object.keys(action.properties).forEach(function (property) {
                candidates.push(Object.assign({}, base, {
                  kind: "style", property: property, value: action.properties[property]
                }));
              });
            } else if (action.type === "class:add") {
              action.classes.forEach(function (className) {
                candidates.push(Object.assign({}, base, { kind: "class", className: className }));
              });
            } else if (action.type === "rewrite") {
              var content = resolveContent(rule, action, element, reason);
              if (content) candidates.push(Object.assign({}, base, {
                kind: action.mode === "outer" ? "outer" : "inner",
                html: content.html,
                replacement: content.element || null
              }));
            } else if (action.type === "text") {
              candidates.push(Object.assign({}, base, { kind: "text", value: action.value }));
            } else {
              candidates.push(Object.assign({}, base, { kind: action.type }));
            }
          });
        });
      });
      return elementCandidates;
    }

    function resolveOperations(elementCandidates, reason) {
      var operations = [];
      elementCandidates.forEach(function (candidates, element) {
        function conflict(winner, loser) { reportConflict(winner, loser, element, reason); }
        var majors = candidates.filter(function (item) { return item.kind === "remove" || item.kind === "outer"; });
        var major = choose(majors, true, conflict);
        if (major) {
          candidates.forEach(function (item) {
            if (item !== major && majors.indexOf(item) === -1) conflict(major, item);
          });
          operations.push(major);
          return;
        }

        var scalar = candidates.filter(function (item) { return item.kind === "inner" || item.kind === "text"; });
        var scalarWinner = choose(scalar, true, conflict);
        if (scalarWinner) operations.push(scalarWinner);

        var moves = candidates.filter(function (item) { return item.kind === "move"; });
        var moveWinner = choose(moves, true, conflict);
        if (moveWinner) operations.push(moveWinner);

        var styles = Object.create(null);
        candidates.filter(function (item) { return item.kind === "style"; }).forEach(function (item) {
          (styles[item.property] || (styles[item.property] = [])).push(item);
        });
        Object.keys(styles).forEach(function (property) {
          operations.push(choose(styles[property], true, conflict));
        });

        var classes = Object.create(null);
        candidates.filter(function (item) { return item.kind === "class"; }).forEach(function (item) {
          if (!classes[item.className] || outranks(item, classes[item.className])) classes[item.className] = item;
        });
        Object.keys(classes).forEach(function (className) { operations.push(classes[className]); });
      });
      return operations;
    }

    function resolveDestination(operation, reason) {
      var destinations = query(root, operation.action.destination, true);
      if (destinations.length === 0) return null;
      if (destinations.length > 1) {
        var error = new Error("Rule \"" + operation.rule.id + "\": move destination is ambiguous");
        reportError(error, {
          ruleId: operation.rule.id,
          reason: reason,
          changedActions: [operation.action],
          oldElement: operation.element,
          newElement: operation.element,
          error: error
        });
        return null;
      }
      var destination = destinations[0];
      if (destination === operation.element || operation.element.contains(destination)) {
        var hierarchyError = new Error("Rule \"" + operation.rule.id + "\": cannot move an element into or next to itself");
        reportError(hierarchyError, {
          ruleId: operation.rule.id,
          reason: reason,
          changedActions: [operation.action],
          oldElement: operation.element,
          newElement: operation.element,
          error: hierarchyError
        });
        return null;
      }
      return destination;
    }

    function needsChange(operation, reason) {
      var element = operation.element;
      if (operation.kind === "remove") return true;
      if (operation.kind === "outer") return element.outerHTML !== operation.html;
      if (operation.kind === "inner") return element.innerHTML !== operation.html;
      if (operation.kind === "text") return element.textContent !== operation.value;
      if (operation.kind === "class") return !element.classList.contains(operation.className);
      if (operation.kind === "style") {
        var current = element.style.getPropertyValue(operation.property);
        return operation.value == null ? current !== "" : current !== operation.value;
      }
      if (operation.kind === "move") {
        operation.destination = resolveDestination(operation, reason);
        if (!operation.destination) return false;
        var destination = operation.destination;
        var position = operation.action.position;
        if (position === "before") return element.nextSibling !== destination;
        if (position === "after") return destination.nextSibling !== element;
        if (position === "prepend") return destination.firstChild !== element;
        return destination.lastChild !== element;
      }
      return false;
    }

    function groupOperations(operations, reason) {
      var groups = [];
      var byElement = new Map();
      operations.forEach(function (operation) {
        if (!needsChange(operation, reason)) return;
        var byRule = byElement.get(operation.element);
        if (!byRule) {
          byRule = Object.create(null);
          byElement.set(operation.element, byRule);
        }
        var group = byRule[operation.rule.id];
        if (!group) {
          group = {
            rule: operation.rule,
            oldElement: operation.element,
            newElement: operation.kind === "outer" ? operation.replacement.cloneNode(true) :
              (operation.kind === "remove" ? null : operation.element),
            operations: [],
            changedActions: []
          };
          byRule[operation.rule.id] = group;
          groups.push(group);
        }
        group.operations.push(operation);
        if (group.changedActions.indexOf(operation.action) === -1) group.changedActions.push(operation.action);
      });
      return groups;
    }

    function detailFor(group, reason) {
      return {
        ruleId: group.rule.id,
        reason: reason,
        changedActions: group.changedActions.slice(),
        oldElement: group.oldElement,
        newElement: group.newElement
      };
    }

    function runHook(group, name, reason) {
      var detail = detailFor(group, reason);
      try {
        if (group.rule.hooks[name]) group.rule.hooks[name](detail);
      } catch (error) {
        detail.error = error;
        reportError(error, detail);
      }
      dispatch(name === "beforeApply" ? "domreconciler:before-apply" : "domreconciler:after-apply",
        detail, name === "beforeApply" ? group.oldElement : group.newElement);
    }

    function applyOperation(operation, group) {
      var element = operation.element;
      var elementInRoot = root.nodeType === 9 ? element && element.ownerDocument === root && element.isConnected :
        element && (element === root || root.contains(element));
      if (!elementInRoot) return false;
      if (operation.kind === "style") {
        var current = element.style.getPropertyValue(operation.property);
        if (operation.value == null) {
          if (current === "") return false;
          element.style.removeProperty(operation.property);
        } else {
          if (current === operation.value) return false;
          element.style.setProperty(operation.property, operation.value);
        }
      } else if (operation.kind === "class") {
        if (element.classList.contains(operation.className)) return false;
        element.classList.add(operation.className);
      } else if (operation.kind === "text") {
        if (element.textContent === operation.value) return false;
        element.textContent = operation.value;
      } else if (operation.kind === "inner") {
        if (element.innerHTML === operation.html) return false;
        element.innerHTML = operation.html;
      } else if (operation.kind === "outer") {
        if (!element.parentNode || element.outerHTML === operation.html) return false;
        var replacement = group.newElement;
        element.parentNode.replaceChild(replacement, element);
      } else if (operation.kind === "remove") {
        if (!element.parentNode) return false;
        element.parentNode.removeChild(element);
        group.newElement = null;
      } else if (operation.kind === "move") {
        var destination = operation.destination;
        var destinationInRoot = root.nodeType === 9 ? destination && destination.ownerDocument === root && destination.isConnected :
          destination && (destination === root || root.contains(destination));
        if (!destinationInRoot) return false;
        var position = operation.action.position;
        if (position === "before") {
          if (element.nextSibling === destination) return false;
          destination.parentNode.insertBefore(element, destination);
        } else if (position === "after") {
          if (destination.nextSibling === element) return false;
          destination.parentNode.insertBefore(element, destination.nextSibling);
        } else if (position === "prepend") {
          if (destination.firstChild === element) return false;
          destination.insertBefore(element, destination.firstChild);
        } else {
          if (destination.lastChild === element) return false;
          destination.appendChild(element);
        }
      } else {
        return false;
      }
      return true;
    }

    function performReconcile(reason) {
      if (destroyed || reconciling) {
        if (!destroyed) schedule(reason);
        return false;
      }
      reconciling = true;
      var groups;
      try {
        groups = groupOperations(resolveOperations(collect(reason), reason), reason);
        if (!groups.length) return false;

        groups.forEach(function (group) { runHook(group, "beforeApply", reason); });
        var hookRecords = observer ? observer.takeRecords() : [];
        if (observer) observer.disconnect();
        groups.forEach(function (group) {
          group.didChange = false;
          group.operations.forEach(function (operation) {
            if (applyOperation(operation, group)) group.didChange = true;
          });
        });
        observe();
        if (hookRecords.length) schedule("hook");
        groups.forEach(function (group) {
          if (group.didChange) runHook(group, "afterApply", reason);
        });
        return groups.some(function (group) { return group.didChange; });
      } finally {
        if (observer && running) observe();
        reconciling = false;
      }
    }

    function addRule(rule) {
      ensureAlive();
      var normalized = validateRule(rule, root, ids, nextOrder++);
      ids.add(normalized.id);
      rules.push(normalized);
      if (running) performReconcile("rule-added");
      return api;
    }

    function removeRule(id) {
      ensureAlive();
      assert(typeof id === "string", "removeRule(id) requires a string id");
      var index = rules.findIndex(function (rule) { return rule.id === id; });
      if (index === -1) return false;
      rules.splice(index, 1);
      ids.delete(id);
      if (running) performReconcile("rule-removed");
      return true;
    }

    api = {
      start: function () {
        ensureAlive();
        if (running) return api;
        running = true;
        observer = observer || new MutationObserver(function (records) {
          if (records.length) schedule("mutation");
        });
        observe();
        performReconcile("start");
        return api;
      },
      stop: function () {
        ensureAlive();
        running = false;
        if (observer) observer.disconnect();
        if (frameId != null) global.cancelAnimationFrame(frameId);
        frameId = null;
        queuedReasons = [];
        return api;
      },
      destroy: function () {
        if (destroyed) return;
        if (running) api.stop();
        destroyed = true;
        rules.length = 0;
        ids.clear();
        observer = null;
      },
      addRule: addRule,
      removeRule: removeRule,
      reconcile: function () {
        ensureAlive();
        return performReconcile("manual");
      }
    };

    var initialRules = options.rules == null ? [] : options.rules;
    assert(Array.isArray(initialRules), "rules must be an array");
    initialRules.forEach(addRule);
    return api;
  }

  global.DOMReconciler = { create: create };
})(globalThis);
